import { useEffect, useState } from 'react';
import { Landmark, Copy, Check, ExternalLink, AlertTriangle, Send, Info, Zap, RefreshCw } from 'lucide-react';
import { Badge } from './ui/badge';
import PetanqueLoader from './PetanqueLoader';
import { Button } from './ui/button';
import { Dialog } from './ui/dialog';
import { Input } from './ui/input';
import { Select } from './ui/select';
import { api } from '../api/sangly';
import { moneyExact, fmtDate } from '../lib/format';
import { LOCAL_ROUTING, LOCAL_COUNTRIES } from '../lib/bank';
import { cn } from '../lib/utils';

const fmtIban = (s) => (s || '').replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();

function CopyBtn({ value, title = 'Copy' }) {
  const [done, setDone] = useState(false);
  if (!value) return null;
  return (
    <button type="button" title={title}
            onClick={() => navigator.clipboard?.writeText(value).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); }).catch(() => {})}
            className="shrink-0 text-muted-foreground hover:text-foreground">
      {done ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function Line({ label, children }) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-start gap-3 py-1.5">
      <div className="pt-px text-[12px] text-muted-foreground">{label}</div>
      <div className="min-w-0 text-[13px]">{children}</div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="px-5 py-3.5">
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" />} {title}
      </div>
      <div className="divide-y divide-border/60">{children}</div>
    </div>
  );
}

/**
 * The payment sheet: everything a payer checks before money leaves - the
 * beneficiary as Odoo knows the vendor, the amount, the communication - then a
 * validate step. Until Airwallex is connected the validate records a manual
 * transfer (made from the bank) and moves the pro-forma to Paid.
 */
// kind: 'proforma' (default) | 'refund' - same sheet, different record and endpoints
export default function PaymentSheet({ proformaId, recordId, kind = 'proforma', open, onClose, onPaid }) {
  const id = recordId ?? proformaId;
  const svc = kind === 'refund' ? api.refunds : api.proformas;
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [accountId, setAccountId] = useState(null);
  const [checked, setChecked] = useState(false);
  const [note, setNote] = useState('');
  const [reference, setReference] = useState('');   // the communication - editable
  const [address, setAddress] = useState({ street: '', postcode: '', city: '', state: '' });   // completes what Odoo lacks
  const STATE_COUNTRIES = ['AU', 'US', 'CA', 'IT'];
  const [needs, setNeeds] = useState({});   // fields Airwallex asked for on the last attempt ({state: {options}})
  // refunds with no Odoo customer: search one and link it
  const [custQ, setCustQ] = useState('');
  const [custHits, setCustHits] = useState([]);
  const [custBusy, setCustBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [entity, setEntity] = useState('COMPANY');   // refunds: the customer may be a person
  const [bank, setBank] = useState({ country: '', routing_value: '', routing_value2: '', bic: '' });   // local (non-IBAN) accounts
  const [name, setName] = useState('');              // refunds: the beneficiary name, editable
  const [paying, setPaying] = useState(false);
  const [payErr, setPayErr] = useState('');
  const [result, setResult] = useState(null);   // the pro-forma after an Airwallex transfer was created
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!open || !id) return;
    setInfo(null); setErr(''); setPayErr(''); setChecked(false); setNote(''); setResult(null); setNeeds({}); setLoading(true);
    svc.paymentInfo(id)
      .then((d) => {
        setInfo(d); setAccountId(d.beneficiary?.accounts?.[0]?.id ?? null); setReference(d.reference || '');
        setAddress({ street: d.beneficiary?.street || '', postcode: d.beneficiary?.zip || '', city: d.beneficiary?.city || '',
                     state: d.beneficiary?.state_code || d.beneficiary?.state || d.beneficiary?.derived_state || '' });
        setEntity(d.entity_type || 'COMPANY'); setName(d.beneficiary?.name || '');
        const a0 = d.beneficiary?.accounts?.[0];
        setBank({ country: a0?.country || d.beneficiary?.country_code || '', routing_value: a0?.routing_value || '', routing_value2: a0?.routing_value2 || '', bic: a0?.bic || '' });
      })
      .catch((e) => setErr(e?.response?.data?.detail || 'Could not load the payment sheet.'))
      .finally(() => setLoading(false));
  }, [open, id, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const q = custQ.trim();
    if (q.length < 2) { setCustHits([]); return undefined; }
    const t = setTimeout(async () => {
      setCustBusy(true);
      try { setCustHits(await api.refunds.odooCustomers(q)); } catch { setCustHits([]); } finally { setCustBusy(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [custQ]);
  const linkCustomer = async (hit) => {
    try { await api.refunds.linkCustomer(id, hit.partner_id); setCustQ(''); setCustHits([]); setReloadKey((k) => k + 1); }
    catch (e) { setPayErr(e?.response?.data?.detail || 'Could not link the customer.'); }
  };

  const isRefund = kind === 'refund';
  // the record: a pro-forma (vendor) or a refund (customer) - same fields the sheet needs
  const raw = info?.record || info?.proforma;
  const pf = raw && (isRefund
    ? { ...raw, supplier_name: raw.client, due_date: null, their_reference: raw.credit_note_ref, our_reference: '' }
    : raw);
  const b = info?.beneficiary;
  const accounts = b?.accounts || [];
  const account = accounts.find((a) => a.id === accountId) || accounts[0] || null;
  const aw = info?.airwallex || { enabled: false };
  const amountOk = pf && Number(pf.amount) > 0;
  const isLocal = account?.type === 'local';
  const routing = isLocal ? (LOCAL_ROUTING[bank.country] || []) : [];
  const localOk = !isLocal || (!!bank.country && routing.every((_, i) => ((i === 0 ? bank.routing_value : bank.routing_value2) || '').trim()));
  // Airwallex needs street + postcode + city (+ state in AU / US / CA); Odoo often lacks them
  const addrCountry = (b?.country_code || account?.country || '').toUpperCase();
  const needsState = STATE_COUNTRIES.includes(addrCountry) || !!needs.state;
  const stateOptions = needs.state?.options || [];
  const addressIncomplete = !!b && (!(b.street && b.zip && b.city && (!needsState || b.state_code || b.state || b.derived_state)) || !!needs.state);
  const addressOk = !aw.enabled || (address.street.trim() && address.postcode.trim() && address.city.trim() && (!needsState || address.state.trim()));
  const canPay = !!pf && amountOk && !!account && checked && !paying && !!reference.trim() && !!addressOk && localOk && (!isRefund || (aw.enabled && !!name.trim()));
  const amountLabel = pf ? moneyExact(pf.amount, pf.currency) : '';

  const pay = async (via) => {
    setPaying(true); setPayErr('');
    try {
      const addr = addressIncomplete ? { street: address.street.trim(), postcode: address.postcode.trim(), city: address.city.trim(), state: address.state.trim() } : null;
      const bankArg = isLocal ? { country: bank.country, routing_value: bank.routing_value.trim(), routing_value2: bank.routing_value2.trim(), bic: bank.bic.trim() } : null;
      const p = isRefund
        ? await svc.pay(id, { account_id: account?.id ?? null, reference: reference.trim(), address: addr, entity_type: entity, name: name.trim(), bank: bankArg })
        : await svc.pay(id, { via, account: account?.number || '', account_id: account?.id ?? null, note: note.trim(), reference: reference.trim(), address: addr, bank: bankArg });
      if ((isRefund || via === 'airwallex') && p.status !== 'paid') setResult({ ...p, supplier_name: p.supplier_name || p.client });   // in flight: show + poll
      else onPaid?.();
    } catch (e) {
      setPayErr(e?.response?.data?.detail || 'Could not execute the payment.');
      const n = e?.response?.data?.needs;
      if (n && Object.keys(n).length) { setNeeds(n); setChecked(false); }   // reveal the field(s) Airwallex wants and let them retry
    }
    finally { setPaying(false); }
  };
  const check = async () => {
    if (!result) return;
    setChecking(true);
    try {
      const p = await svc.checkTransfer(id);
      setResult(p);
      if (p.status === 'paid') onPaid?.();
    } catch { /* keep the last state */ } finally { setChecking(false); }
  };
  // poll while the transfer is in flight (sandbox settles in seconds, prod in hours)
  useEffect(() => {
    if (!result?.in_payment) return undefined;
    const t = setInterval(check, 5000);
    return () => clearInterval(t);
  }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onClose={onClose} className="max-w-xl p-0">
      <div className="p-4 pr-10 sm:p-5 sm:pr-11">
        <div className="text-lg font-semibold">Payment</div>
        <div className="mt-0.5 text-[12px] text-muted-foreground">
          Check every line as you would on the bank&apos;s confirmation screen, then validate.
        </div>
      </div>

      {loading || (!info && !err) ? (
        <div className="grid h-48 place-items-center border-t border-border"><PetanqueLoader size={34} roll /></div>
      ) : err ? (
        <div className="border-t border-border p-5 text-[13px] text-rose-600">{err}</div>
      ) : (
        <div className="max-h-[calc(100dvh-10rem)] overflow-y-auto">
          {/* amount hero */}
          <div className="border-y border-border bg-muted/30 px-5 py-4 text-center">
            <div className={cn('text-3xl font-semibold tnum', !amountOk && 'text-amber-600')}>
              {amountOk ? amountLabel : 'Amount to fill in'}
            </div>
            <div className="mt-0.5 text-[13px] text-muted-foreground">
              to <span className="font-medium text-foreground">{pf.supplier_name}</span>
              {pf.due_date && <> · deadline {fmtDate(pf.due_date)}</>}
            </div>
          </div>

          <div className="divide-y divide-border">
            <Section title="Beneficiary" icon={Landmark}>
              {!b ? (
                <div className="flex items-start gap-2 py-1.5 text-[13px] text-amber-700">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{info.beneficiary_error || 'No vendor details available.'}</span>
                </div>
              ) : (
                <>
                  <Line label="Name">
                    {isRefund ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 min-w-[200px] flex-1 text-[12px]" placeholder="Name on the bank account" />
                        <span className="inline-flex rounded-md border border-border bg-card p-0.5 text-[11px] font-medium">
                          {[['PERSONAL', 'Person'], ['COMPANY', 'Company']].map(([k, l]) => (
                            <button key={k} type="button" onClick={() => setEntity(k)}
                                    className={cn('rounded px-2 py-0.5', entity === k ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:text-foreground')}>{l}</button>
                          ))}
                        </span>
                      </div>
                    ) : (
                      <>
                        <span className="font-medium">{b.name}</span>
                        {account?.holder && account.holder !== b.name && (
                          <span className="text-muted-foreground"> · account holder {account.holder}</span>
                        )}
                      </>
                    )}
                  </Line>
                  <Line label={isLocal ? 'Account' : 'IBAN'}>
                    {isLocal ? (
                      <div>
                        <div className="font-mono">{account.display || account.number}</div>
                        <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                          <Select value={bank.country} onChange={(e) => setBank((b) => ({ ...b, country: e.target.value }))} className={cn('h-8 w-full text-[12px]', !bank.country && 'border-amber-400')}>
                            <option value="">Bank country…</option>
                            {LOCAL_COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                          </Select>
                          <Input value={bank.bic} onChange={(e) => setBank((b) => ({ ...b, bic: e.target.value }))} placeholder="BIC / SWIFT (optional)" className="h-8 font-mono text-[12px]" />
                          {routing.map((r, i) => (
                            <Input key={r.label} value={i === 0 ? bank.routing_value : bank.routing_value2}
                                   onChange={(e) => setBank((b) => ({ ...b, [i === 0 ? 'routing_value' : 'routing_value2']: e.target.value }))}
                                   placeholder={`${r.label} · ${r.placeholder}`}
                                   className={cn('h-8 font-mono text-[12px]', !((i === 0 ? bank.routing_value : bank.routing_value2) || '').trim() && 'border-amber-400')} />
                          ))}
                        </div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">Not an IBAN - Airwallex pays it as a local account with the country&apos;s routing code.</div>
                      </div>
                    ) : accounts.length === 0 ? (
                      <span className="inline-flex items-center gap-1.5 text-amber-700">
                        <AlertTriangle className="h-3.5 w-3.5" /> {isRefund ? 'No IBAN typed on this refund - add it via Edit first' : 'No bank account on the vendor in Odoo'}
                      </span>
                    ) : accounts.length > 1 ? (
                      <Select value={accountId ?? ''} onChange={(e) => setAccountId(Number(e.target.value))} className="h-8 w-full text-[12px]">
                        {accounts.map((a) => <option key={a.id} value={a.id}>{fmtIban(a.number)}{a.bank ? ` · ${a.bank}` : ''}</option>)}
                      </Select>
                    ) : (
                      <span className="inline-flex max-w-full items-center gap-1.5">
                        <span className="truncate font-mono">{fmtIban(account.number)}</span>
                        <CopyBtn value={account.number} title="Copy IBAN" />
                      </span>
                    )}
                  </Line>
                  {account && (
                    <Line label="BIC · bank">
                      {account.bic ? <span className="font-mono">{account.bic}</span> : <span className="text-muted-foreground">no BIC</span>}
                      {account.bank && <span className="text-muted-foreground"> · {account.bank}</span>}
                    </Line>
                  )}
                  <Line label="Address">
                    {addressIncomplete && aw.enabled ? (
                      <div>
                        <div className="grid grid-cols-[1fr_90px_1fr] gap-1.5">
                          <Input value={address.street} onChange={(e) => setAddress((a) => ({ ...a, street: e.target.value }))}
                                 placeholder="Street and number" className={cn('h-8 text-[12px]', !address.street.trim() && 'border-amber-400')} />
                          <Input value={address.postcode} onChange={(e) => setAddress((a) => ({ ...a, postcode: e.target.value }))}
                                 placeholder="Postcode" className={cn('h-8 text-[12px]', !address.postcode.trim() && 'border-amber-400')} />
                          <Input value={address.city} onChange={(e) => setAddress((a) => ({ ...a, city: e.target.value }))}
                                 placeholder="City" className={cn('h-8 text-[12px]', !address.city.trim() && 'border-amber-400')} />
                          {needsState && (stateOptions.length ? (
                            <Select value={address.state} onChange={(e) => setAddress((a) => ({ ...a, state: e.target.value }))}
                                    className={cn('h-8 w-full text-[12px]', !address.state.trim() && 'border-amber-400')}>
                              <option value="">State / province…</option>
                              {stateOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                            </Select>
                          ) : (
                            <Input value={address.state} onChange={(e) => setAddress((a) => ({ ...a, state: e.target.value }))}
                                   placeholder={addrCountry === 'AU' ? 'State (VIC, NSW…)' : addrCountry === 'US' ? 'State (CA, NY…)' : addrCountry === 'CA' ? 'Province (ON, QC…)' : addrCountry === 'IT' ? 'Province (TV, MI, RM…)' : 'State / province'}
                                   className={cn('h-8 text-[12px]', !address.state.trim() && 'border-amber-400')} />
                          ))}
                        </div>
                        <div className="mt-0.5 text-[11px] text-amber-700">
                          {needs.state
                            ? 'Airwallex asks for the state / province of this address - pick it and pay again.'
                            : <>Incomplete in Odoo{b.country ? ` (${b.country})` : ''} - Airwallex needs it. Fill it in for this payment and fix the partner.</>}
                        </div>
                      </div>
                    ) : (
                      [b.street, [b.zip, b.city].filter(Boolean).join(' '), b.country].filter(Boolean).join(', ')
                        || <span className="text-muted-foreground">-</span>
                    )}
                  </Line>
                  {b.vat && <Line label="VAT"><span className="font-mono">{b.vat}</span></Line>}
                  {isRefund && !b.partner_id && (
                    <div className="py-2">
                      <div className="mb-1 text-[12px] text-amber-700">No Odoo customer linked - search one to pull the address, country and email.</div>
                      <Input value={custQ} onChange={(e) => setCustQ(e.target.value)} placeholder="Customer name, email, or invoice / credit note number…" className="h-8 text-[12px]" />
                      {custBusy && <div className="mt-1 text-[11px] text-muted-foreground">searching Odoo…</div>}
                      {custHits.length > 0 && (
                        <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-pop">
                          {custHits.map((h) => (
                            <button key={h.partner_id} type="button" onClick={() => linkCustomer(h)}
                                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] hover:bg-muted">
                              <span className="truncate font-medium">{h.name}</span>
                              <span className="truncate text-muted-foreground">{h.via ? `via ${h.via}` : [h.city, h.country].filter(Boolean).join(', ')}{h.email ? ` · ${h.email}` : ''}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 pt-1.5 text-[11px] text-muted-foreground">
                    <span>{isRefund ? (b.partner_id ? 'Source: the customer in Odoo + the IBAN on this refund' : 'Source: this refund (no Odoo customer found for the credit note)') : 'Source: the vendor\'s partner record in Odoo'}</span>
                    {info.odoo_partner_url && (
                      <a href={info.odoo_partner_url} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 text-primary hover:underline">
                        Open in Odoo <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </>
              )}
            </Section>

            <Section title="Transfer" icon={Send}>
              <Line label="Amount">
                <span className="font-semibold tnum">{amountOk ? amountLabel : '-'}</span>
                {aw.deliver_currency && aw.deliver_currency !== pf.currency && (
                  <div className="mt-0.5 text-[11px] text-sky-800">
                    The {bank.country || account?.country} account can only receive {aw.deliver_currency}: the {isRefund ? 'customer' : 'vendor'} gets {amountLabel} worth of {aw.deliver_currency}, converted by Airwallex at today&apos;s rate.
                  </div>
                )}
              </Line>
              <Line label="Communication">
                <div className="flex items-center gap-1.5">
                  <Input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={140}
                         placeholder="What the vendor sees on their statement"
                         className={cn('h-8 font-mono text-[12px]', !reference.trim() && 'border-amber-400')} />
                  <CopyBtn value={reference} title="Copy the communication" />
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {!reference.trim()
                    ? <span className="text-amber-700">Empty - the {isRefund ? 'customer' : 'vendor'} won&apos;t be able to match the payment.</span>
                    : reference.trim() === (info.default_reference || '')
                      ? <>{isRefund ? 'The credit note number.' : 'Their order no. + our PO.'} Edit if something specific was asked for.</>
                      : <>Edited · <button type="button" onClick={() => setReference(info.default_reference || '')} className="text-primary hover:underline">reset to {info.default_reference || 'default'}</button></>}
                </div>
              </Line>
              <Line label="From">
                {info.payer?.name} · {info.payer?.wallet}
                {aw.enabled && aw.balance != null && (
                  <span className="text-muted-foreground"> · available {moneyExact(aw.balance, aw.source_currency || 'EUR')}</span>
                )}
                {aw.enabled && pf.currency !== 'EUR' && (
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    {aw.source_currency === pf.currency
                      ? `Paid from the ${pf.currency} balance - no conversion.`
                      : `No ${pf.currency} balance to cover it - funded from EUR, converted by Airwallex at today's rate.`}
                  </div>
                )}
              </Line>
              <Line label="Execution">{fmtDate(new Date().toISOString())} · today</Line>
              {aw.enabled && (
                <Line label="Rail">
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    <Zap className="h-3.5 w-3.5 text-primary" /> Airwallex
                    <Badge variant={aw.environment === 'sandbox' ? 'warning' : 'success'}>{aw.environment}</Badge>
                    <span className="text-muted-foreground">
                      · beneficiary {aw.beneficiary_saved ? `saved (${aw.method})` : 'will be created from Odoo (SEPA, else SWIFT)'}
                    </span>
                  </span>
                  {aw.error && <div className="mt-0.5 text-[12px] text-rose-600">{aw.error}</div>}
                </Line>
              )}
            </Section>
          </div>

          {(info.warnings?.length > 0 || !aw.enabled) && (
            <div className="space-y-1.5 border-t border-border px-5 py-3">
              {info.warnings?.filter((w) => !(localOk && /looks wrong: .*(missing|should be)/i.test(w))).map((w) => (
                <div key={w} className="flex items-start gap-2 text-[12px] text-amber-700">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {w}
                </div>
              ))}
              {!aw.enabled && (
                <div className="flex items-start gap-2 text-[12px] text-sky-800">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{aw.reason} Make the transfer from the bank with the details above, then record it here.</span>
                </div>
              )}
            </div>
          )}

          <div className="border-t border-border px-5 py-3">
            <label className="flex cursor-pointer items-start gap-2 text-[13px]">
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
                     className="mt-0.5 h-4 w-4 rounded border-border accent-[hsl(var(--primary))]" />
              <span>I checked the beneficiary, the amount and the communication.</span>
            </label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for the log (optional) - e.g. bank, execution date"
                   className="mt-2 h-8 text-[12px]" />
            {payErr && <div className="mt-2 text-[12px] text-rose-600">{payErr}</div>}
          </div>

          {result ? (
            <div className="border-t border-border bg-emerald-500/5 px-5 py-4">
              <div className="flex items-center gap-2 text-[13px]">
                <Zap className="h-4 w-4 text-primary" />
                <span className="font-medium">Transfer {result.airwallex_short_reference || result.airwallex_transfer_id} sent to Airwallex</span>
                <Badge variant={result.status === 'paid' ? 'success' : 'info'}>{result.airwallex_status || '…'}</Badge>
                {result.in_payment && (
                  <button type="button" onClick={check} disabled={checking} title="Re-read the status"
                          className="ml-auto inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground">
                    <RefreshCw className={cn('h-3.5 w-3.5', checking && 'animate-spin')} /> refresh
                  </button>
                )}
              </div>
              <p className="mt-1 text-[12px] text-muted-foreground">
                {result.status === 'paid'
                  ? `The money has left the wallet - the ${isRefund ? 'refund' : 'pro-forma'} is marked paid.`
                  : `Waiting for Airwallex to process it. The ${isRefund ? 'refund' : 'pro-forma'} stays "in payment" and flips to paid on its own; you can close this.`}
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3 border-t border-border px-5 py-3">
              <button onClick={onClose} className="text-[13px] text-muted-foreground hover:underline">Cancel</button>
              {aw.enabled && !isRefund && (
                <button type="button" onClick={() => pay('manual')} disabled={!canPay}
                        className="ml-auto text-[12px] text-muted-foreground hover:underline disabled:opacity-50">
                  Record a manual transfer instead
                </button>
              )}
              <Button className={cn(!aw.enabled && 'ml-auto')} onClick={() => pay(aw.enabled ? 'airwallex' : 'manual')} disabled={!canPay}>
                {paying ? <PetanqueLoader size={16} /> : aw.enabled ? <Zap className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                {aw.enabled ? `Pay ${amountLabel} via Airwallex` : isRefund ? 'Airwallex not connected' : `Validate · paid ${amountOk ? amountLabel : ''}`}
              </Button>
            </div>
          )}
          {result && (
            <div className="flex items-center border-t border-border px-5 py-3">
              <button onClick={onClose} className="ml-auto text-[13px] font-medium text-muted-foreground hover:underline">Close</button>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
